import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { AlertTriangle, Clock } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

type AdminCheckState = 'loading' | 'authorized' | 'unauthorized' | 'error';

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading, sessionExpired } = useAuth();
  const navigate = useNavigate();
  const [adminCheck, setAdminCheck] = useState<AdminCheckState>('loading');
  const [adminError, setAdminError] = useState<string | null>(null);
  // ID des zuletzt erfolgreich verifizierten Admins. Verhindert, dass ein
  // TOKEN_REFRESHED (z. B. beim Zurückkehren in den Tab) die Prüfung erneut
  // anstößt, den Lade-Bildschirm zeigt und damit das Dashboard unmountet.
  const verifiedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (loading || sessionExpired) return;

    if (!user) {
      verifiedUserIdRef.current = null;
      setAdminCheck('unauthorized');
      setAdminError(null);
      return;
    }

    // Derselbe Nutzer wurde bereits als Admin bestätigt — nichts zu tun.
    if (verifiedUserIdRef.current === user.id) return;

    let cancelled = false;

    const checkAdminRole = async () => {
      setAdminCheck('loading');
      setAdminError(null);

      try {
        const { data: profile, error } = await supabase
          .from('student_profiles')
          .select('role')
          .eq('id', user.id)
          .maybeSingle();

        if (cancelled) return;

        if (error) throw error;

        if (profile?.role === 'admin') {
          verifiedUserIdRef.current = user.id;
          setAdminCheck('authorized');
          return;
        }

        verifiedUserIdRef.current = null;
        setAdminCheck('unauthorized');
      } catch (error) {
        if (cancelled) return;
        if (import.meta.env.DEV) console.error('Admin role check failed:', error);
        setAdminError('Admin-Berechtigung konnte nicht geprüft werden. Bitte versuche es erneut.');
        setAdminCheck('error');
      }
    };

    checkAdminRole();

    return () => {
      cancelled = true;
    };
  }, [loading, sessionExpired, user]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#970000] mx-auto mb-4"></div>
          <p className="text-gray-600">Authentifizierung wird überprüft...</p>
        </div>
      </div>
    );
  }

  if (sessionExpired) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock className="h-8 w-8 text-orange-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Session abgelaufen</h2>
          <p className="text-gray-600 mb-6">
            Ihre Session ist aus Sicherheitsgründen abgelaufen. Bitte melden Sie sich erneut an.
          </p>
          <button
            onClick={() => navigate('/admin/login', { replace: true })}
            className="inline-flex items-center justify-center rounded-full bg-[#970000] px-6 py-3 text-sm font-semibold text-white hover:bg-[#7a0000] transition-colors"
          >
            Neu anmelden
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  if (adminCheck === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#970000] mx-auto mb-4"></div>
          <p className="text-gray-600">Admin-Berechtigung wird überprüft...</p>
        </div>
      </div>
    );
  }

  if (adminCheck === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-8 w-8 text-[#970000]" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Prüfung fehlgeschlagen</h2>
          <p className="text-gray-600 mb-6">{adminError}</p>
          <button
            onClick={() => navigate('/admin/login', { replace: true })}
            className="inline-flex items-center justify-center rounded-full bg-[#970000] px-6 py-3 text-sm font-semibold text-white hover:bg-[#7a0000] transition-colors"
          >
            Zum Admin-Login
          </button>
        </div>
      </div>
    );
  }

  if (adminCheck !== 'authorized') {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
